import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Paper, Typography, Chip, Button, TextField, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, FormControl, InputLabel, Select, CircularProgress, Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  RestartAlt as ResetIcon,
  CheckCircle as SuccessIcon,
  ErrorOutline as ErrorIcon,
  PersonOutline as PersonIcon,
} from "@mui/icons-material";
import HeaderBannerPage from "../../components/HeaderBannerPage";
import { NAVY, cardSx, headCellSx, EmployeeSelect, fmtDate, toKey } from "../../components/leave/LeaveCommon";
import { getAllEmployees } from "../../actions/EmployeeRegisterAction";
import { extractRows } from "../../service/Leave/leaveHelpers";
import { fetchLeaveTypes, fetchUserLeaveTypes, clearUserLeaveTypes } from "../../actions/Leave/LeaveTypeAction";
import { fetchLeaveAdjustments, clearLeaveAdjustments, addLeaveAdjustment } from "../../actions/Leave/LeaveAdjustmentAction";

const ROWS = 8;
const ADJUSTMENT_TYPES = [
  { value: "CREDIT", label: "Credit (add days)" },
  { value: "DEBIT", label: "Debit (deduct days)" },
  // { value: "CORRECTION", label: "Correction" },
  // { value: "CARRY_FORWARD", label: "Carry Forward" },
  // { value: "ENCASHMENT", label: "Encashment" },
];
const ADJ_LABEL = Object.fromEntries(ADJUSTMENT_TYPES.map((t) => [t.value, t.label.split(" (")[0]]));
const ADJ_POSITIVE = ["CREDIT", "CARRY_FORWARD", "INCREASE"];
const currentMonth = () => toKey(new Date()).slice(0, 7); // YYYY-MM

const emptyForm = () => ({
  employee: null, typeId: "", date: toKey(new Date()), days: "1", adjType: "CREDIT", reason: "",
});

const AdjustmentChip = ({ type }) => {
  const key = String(type || "").toUpperCase();
  const neutral = key === "CORRECTION";
  const up = ADJ_POSITIVE.includes(key);
  return (
    <Chip
      label={ADJ_LABEL[key] || type || "—"} size="small"
      sx={{
        fontWeight: 700, fontSize: 11,
        color: neutral ? "#475569" : up ? "#15803d" : "#b91c1c",
        backgroundColor: neutral ? "#e2e8f0" : up ? "#dcfce7" : "#fee2e2",
      }}
    />
  );
};

const LeaveAdjustments = () => {
  const dispatch = useDispatch();

  // ── Redux data ──────────────────────────────────────────────
  const { data: rawEmployees, error: empError } = useSelector((s) => s.getAllEmployees) || {};
  const { data: leaveTypes = [] } = useSelector((s) => s.leaveTypes) || {};
  const { userData: userTypes = [], userLoading: userTypesLoading } = useSelector((s) => s.leaveTypes) || {};
  const { data: adjustments = [], loading, error, adding } = useSelector((s) => s.leaveAdjustments) || {};
  const createdBy = useSelector((s) => s.auth?.serviceNo) || "";

  // ── Filters (default: no employee yet, current month, all leave types) ──
  const [employee, setEmployee] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);

  // ── Add dialog ──────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState({ open: false, ok: true, message: "" });

  // Load the employee list and leave type master once. Adjustments are NOT loaded here.
  useEffect(() => {
    dispatch(getAllEmployees());
    dispatch(fetchLeaveTypes());
    dispatch(clearLeaveAdjustments());
  }, [dispatch]);

  const employeeOptions = useMemo(() => {
    const rows = extractRows(rawEmployees);
    return rows
      .map((e) => {
        const serviceNo = String(e.serviceNo ?? e.ServiceNo ?? "").trim();
        return {
          serviceNo,
          name: e.reportName || e.ReportName || e.name || serviceNo,
          department: e.department || e.DepartmentCode || e.Designation || "",
        };
      })
      .filter((e) => e.serviceNo);
  }, [rawEmployees]);

  const typeNameById = useMemo(() => Object.fromEntries(leaveTypes.map((t) => [t.id, t.name])), [leaveTypes]);
  const empNameByNo = useMemo(() => Object.fromEntries(employeeOptions.map((e) => [e.serviceNo, e.name])), [employeeOptions]);

  // Fetch the adjustments of the picked employee (this is the only place the list is loaded)
  const pickEmployee = (emp) => {
    setEmployee(emp);
    setPage(0);
    if (emp) dispatch(fetchLeaveAdjustments(emp.serviceNo));
    else dispatch(clearLeaveAdjustments());
  };

  const filtered = useMemo(
    () =>
      adjustments
        .filter((a) => {
          if (month && !a.date.startsWith(month)) return false;
          if (typeFilter !== "all" && a.leaveTypeId !== typeFilter) return false;
          return true;
        })
        .sort((a, b) => b.date.localeCompare(a.date)),
    [adjustments, month, typeFilter]
  );
  const rows = filtered.slice(page * ROWS, page * ROWS + ROWS);
  const hasFilter = !!employee || month !== currentMonth() || typeFilter !== "all";

  const resetFilters = () => {
    pickEmployee(null); setMonth(currentMonth()); setTypeFilter("all");
  };

  // ── Dialog helpers ──────────────────────────────────────────
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const openDialog = () => {
    setForm(emptyForm()); setErrors({});
    dispatch(clearUserLeaveTypes());
    setOpen(true);
  };
  const closeDialog = () => setOpen(false);

  // The leave types offered depend on the employee picked in the dialog
  const pickFormEmployee = (emp) => {
    setForm((f) => ({ ...f, employee: emp, typeId: "" }));
    setErrors((e) => ({ ...e, employee: undefined, typeId: undefined }));
    if (emp) dispatch(fetchUserLeaveTypes(emp.serviceNo));
    else dispatch(clearUserLeaveTypes());
  };

  const submit = async () => {
    const days = Number(form.days);
    const e = {};
    if (!form.employee) e.employee = "Select an employee";
    if (!form.typeId) e.typeId = "Select a leave type";
    if (!form.date) e.date = "Select a date";
    if (!form.days || isNaN(days) || days < 0.5) e.days = "Minimum is 0.5 days";
    else if (days * 2 !== Math.floor(days * 2)) e.days = "Use whole or half days (e.g. 1 or 1.5)";
    setErrors(e);
    if (Object.keys(e).length) return;

    const res = await dispatch(
      addLeaveAdjustment({
        serviceNo: form.employee.serviceNo,
        leaveType: form.typeId,
        date: form.date,
        days: form.adjType === "DEBIT" ? -days : days,
        adjustmentType: form.adjType,
        reason: form.reason.trim(),
        createdBy,
      })
    );

    if (res.ok) {
      setOpen(false);
      // show the new record: pick that employee and jump to the record's month
      setMonth(form.date.slice(0, 7));
      setTypeFilter("all");
      pickEmployee(form.employee);
    }
    setResult({ open: true, ok: res.ok, message: res.message });
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <HeaderBannerPage />
      <Box sx={{ maxWidth: 1400, mx: "auto", mt: 4, px: 2, pb: 4 }}>
        {/* Banner */}
        <Paper
          elevation={0}
          sx={{
            p: 3, mb: 3, borderRadius: "12px",
            background: "linear-gradient(135deg, #1e0153 0%, #154c79 100%)",
            color: "#fff", boxShadow: "0px 4px 12px rgba(30, 1, 83, 0.15)",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2,
          }}
        >
          <Box>
            <Chip
              label="LEAVE MANAGEMENT" size="small"
              sx={{ fontWeight: "bold", mb: 1, fontSize: "11px", letterSpacing: "0.5px", backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
              Leave Adjustments
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, fontSize: "13px" }}>
              Manually increase or decrease an employee&apos;s leave balance
            </Typography>
          </Box>
          <Button
            onClick={openDialog} startIcon={<AddIcon />} variant="contained"
            sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#fff", color: NAVY, "&:hover": { backgroundColor: "#e2e8f0" } }}
          >
            Add Adjustment
          </Button>
        </Paper>

        {empError && <Alert severity="error" sx={{ mb: 2 }}>Could not load employees: {String(empError)}</Alert>}

        {/* Filters */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, alignItems: "center" }}>
          <EmployeeSelect
            label="Employee" placeholder="Select an employee" options={employeeOptions}
            value={employee} onChange={pickEmployee} sx={{ width: 300 }}
          />
          <TextField
            size="small" type="month" label="Month" value={month}
            onChange={(e) => { setMonth(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Leave Type</InputLabel>
            <Select value={typeFilter} label="Leave Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
              <MenuItem value="all">All Leave Types</MenuItem>
              {leaveTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button
            onClick={resetFilters} disabled={!hasFilter} startIcon={<ResetIcon />}
            sx={{ textTransform: "none", fontWeight: 600, color: "#334155", border: "1px solid #e2e8f0", height: 40, px: 2 }}
          >
            Reset
          </Button>
        </Box>

        {/* Table */}
        <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #eef0f4", display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>Adjustment Records</Typography>
            {employee && !loading && (
              <Typography sx={{ fontSize: 12.5, color: "#64748b", alignSelf: "center" }}>{filtered.length} record(s)</Typography>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  {["Employee", "Leave Type", "Date", "Days", "Adjustment Type", "Reason"].map((h) => (
                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!employee && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                      <PersonIcon sx={{ display: "block", mx: "auto", mb: 0.5, color: "#cbd5e1" }} />
                      Select an employee to view their leave adjustments
                    </TableCell>
                  </TableRow>
                )}
                {employee && loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}><CircularProgress size={26} /></TableCell>
                  </TableRow>
                )}
                {employee && !loading && error && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#dc2626" }}>{String(error)}</TableCell>
                  </TableRow>
                )}
                {employee && !loading && !error && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#94a3b8" }}>
                      No adjustments found for the selected filters.
                    </TableCell>
                  </TableRow>
                )}
                {employee && !loading && rows.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ fontSize: 13 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{empNameByNo[a.serviceNo] || employee.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{a.serviceNo}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{typeNameById[a.leaveTypeId] || `Type ${a.leaveTypeId}`}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{fmtDate(a.date)}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>{Number(a.days).toFixed(1)}</TableCell>
                    <TableCell><AdjustmentChip type={a.adjType} /></TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#475569", maxWidth: 320 }}>{a.reason || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {employee && !loading && filtered.length > ROWS && (
            <TablePagination
              component="div" count={filtered.length} page={page} onPageChange={(e, p) => setPage(p)}
              rowsPerPage={ROWS} rowsPerPageOptions={[ROWS]}
            />
          )}
        </Paper>
      </Box>

      {/* Add Adjustment dialog */}
      <Dialog open={open} onClose={adding ? undefined : closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700, color: NAVY }}>
          Add Adjustment
          <IconButton size="small" onClick={closeDialog} disabled={adding}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}>
          <EmployeeSelect
            label="Select Employee" required options={employeeOptions} value={form.employee}
            onChange={pickFormEmployee} error={!!errors.employee} helperText={errors.employee}
          />
          <TextField
            select size="small" required label="Leave Type" value={form.typeId}
            onChange={(e) => set("typeId", e.target.value)}
            error={!!errors.typeId}
            helperText={
              errors.typeId ||
              (!form.employee ? "Select an employee first"
                : userTypesLoading ? "Loading leave types..."
                : userTypes.length === 0 ? "No leave types available for this employee" : "")
            }
            disabled={!form.employee || userTypesLoading || userTypes.length === 0}
          >
            {userTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              size="small" required type="date" label="Date" value={form.date}
              onChange={(e) => set("date", e.target.value)} InputLabelProps={{ shrink: true }}
              error={!!errors.date} helperText={errors.date} sx={{ flex: 1 }}
            />
            <TextField
              size="small" required type="number" label="Days" value={form.days}
              onChange={(e) => set("days", e.target.value)} inputProps={{ min: 0.5, step: 0.5 }}
              error={!!errors.days} helperText={errors.days} sx={{ width: 120 }}
            />
          </Box>
          <TextField
            select size="small" required label="Adjustment Type" value={form.adjType}
            onChange={(e) => set("adjType", e.target.value)}
          >
            {ADJUSTMENT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField
            size="small" label="Reason" multiline minRows={2} value={form.reason}
            onChange={(e) => set("reason", e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog} disabled={adding} sx={{ textTransform: "none", color: "#475569" }}>Cancel</Button>
          <Button
            onClick={submit} disabled={adding} variant="contained"
            sx={{ textTransform: "none", fontWeight: 700, backgroundColor: NAVY, "&:hover": { backgroundColor: "#0f2140" } }}
          >
            {adding ? "Adding..." : "Add Adjustment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result dialog */}
      <Dialog open={result.open} onClose={() => setResult({ ...result, open: false })} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: "center", pt: 4 }}>
          {result.ok
            ? <SuccessIcon sx={{ fontSize: 56, color: "#16a34a" }} />
            : <ErrorIcon sx={{ fontSize: 56, color: "#dc2626" }} />}
          <Typography sx={{ fontWeight: 700, fontSize: 18, mt: 1 }}>{result.ok ? "Success!" : "Error"}</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 14, mt: 0.5 }}>{result.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => setResult({ ...result, open: false })} variant="contained"
            sx={{ textTransform: "none", fontWeight: 700, px: 4, backgroundColor: result.ok ? NAVY : "#dc2626" }}
          >
            {result.ok ? "OK" : "Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveAdjustments;